import React from 'react'
import { get, set } from 'lodash'
import moment from 'moment'
import { Details, Toaster } from '@ui'
import api, { FormErrors, INewLease } from '@api'
import { ILeaseHook } from './hooks/useLease'
import isString from '@utils/isString'

interface IProps {
  children: React.ReactNode
  hook: ILeaseHook
}

export const requiredFields = [
  { key: 'floor|building', label: 'Location' },
  { key: 'cost', label: 'Cost' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'endDate', label: 'End Date' },
]
export type LeaseFormErrors = FormErrors<INewLease>

export default function Form({ hook, children }: IProps) {
  const submit = () => {
    if (!hook.lease) return
    const { lease } = hook

    const err: LeaseFormErrors = {}
    for (const d of [
      ['startDate', 'Start Date'],
      ['endDate', 'End Date'],
    ])
      if (!isString(lease[d[0]]) && isNaN(lease[d[0]])) {
        err[d[0]] = `${d[1]} is invalid`
      }

    if (moment(lease.endDate).isBefore(lease.startDate))
      err.endDate = 'End Date must be after Start Date'

    if (+get(lease, 'cost') < 0) set(err, 'cost', 'Cost must be positive')
    if (+get(lease, 'metadata.otherCost') < 0)
      set(err, 'metadata.otherCost', 'Other currency cost must be positive')
    if (+get(lease, 'metadata.leaseArea') < 0)
      set(err, 'metadata.leaseArea', 'Lease Area must be positive')
    if (+get(lease, 'metadata.usableLeaseArea') < 0)
      set(err, 'metadata.usableLeaseArea', 'Usable Lease Area must be positive')

    for (const { key, label } of requiredFields) {
      if (!key.split('|').find((k) => get(lease, k))) err[key] = `${label} is required`
    }

    if (lease.endAlert && moment().isSameOrAfter(lease.endDate, 'day'))
      err.endDate = "End Date must be in the future if it's notification is on"
    if (lease.noticeDate && moment().isSameOrAfter(lease.noticeDate, 'day'))
      err.noticeDate = 'Notice Date must be in the future'

    hook.setErrors(err)
    if (Object.values(err).filter((v) => v).length) return

    hook.setSaving(true)

    api.lease
      .save(hook.lease)
      .then(({ lease }) => {
        hook.setLease(lease)
        if (hook.localFiles.length) {
          api.lease.assets
            .add(lease.id, hook.localFiles)
            .then(() => {
              hook.setLocalFiles([])
              Toaster.success({ message: 'Changes saved.' })
              hook.setEditing(false)
              hook.reload()
            })
            .catch((e: any) => {
              Toaster.fail({
                message: 'There was a problem uploading the attachments.',
              })
              hook.setSaving(false)
            })
        } else {
          hook.reload()
          hook.setEditing(false)
        }
      })
      .catch((error: any) => {
        hook.setErrors({
          ...err,
          ...error.graphQLErrors[0].invalidArguments,
        })
        Toaster.fail({ error })
      })
  }

  return (
    <Details
      onSubmit={(e: any) => {
        e.preventDefault()
        submit()
      }}
    >
      {children}
    </Details>
  )
}
